import { NEW_LENDER, EXISTING_LENDER } from "typeConstants/customerTypes";
import { caseTypes } from "components/Utility/Objects";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_NUMBER_RAW,
  DATA_TYPE_NUMBER_HUNDREDTHS,
  DATA_TYPE_NUMBER_TENTHS,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_MONEY
} from "typeConstants/dataTypes";
import xlsx from "xlsx";

export const clearNullFromColumnDefs = colDefs => {
  return (
    colDefs &&
    colDefs.map(cd =>
      Object.keys(cd).reduce((acc, key) => {
        if (cd[key] !== null) acc[key] = cd[key];
        return acc;
      }, {})
    )
  );
};

/*
call this with the count of items you want to display in a grid and then the current index of the item that you want the md-cell width for
this is used to display cells in an md grid that will be 1/3 the full width when they can be, and 1/2 otherwise
  examples:
  a grid with 5 cells will look like:
  1st row: 1/3 1/3 1/3
  2nd row: 1/2     1/2

  a grid with 8 cells will look like:
  1st row: 1/3 1/3 1/3
  2nd row: 1/3 1/3 1/3
  3rd row: 1/2     1/2
  */
export const getVariableGridCellSize = (l, i) => l - i <= (2 - (l - 1) % 3) * 2 ? 6 : 4;

export const getSearchParameter = (obj, key) => {
  if (!key) key = "state.searchText";

  let result = key.split(".").reduce((o, x) => {
    return (typeof o == "undefined" || o === null) ? o : o[x];
  }, obj);

  if (result && (typeof result === "string")) {
    return result;
  }
  return null;
};

//Returns true or false as to whether an object contains a specified key
export const checkObjectForKey = (obj, key) => {
  return obj ? Object.keys(obj).includes(key) : false;
};

//get a hash code from a string. will most of the time end up unique
//please do not use this for any sort of security, i initially needed this to provide react with unique keys
//and i didnt want to use large labels with spaces as the key so i figured i would get a hash instead
export const getHashCode = s => {
  let hash = 0, chr;
  if (!s || s.length === 0) return hash;
  for (let i = 0; i < s.length; i++) {
    chr = s.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const isCustomer = roles => roles && Array.isArray(roles)
  ? roles.includes("MuellerCustomer") || roles.includes("MuellerCustomerManager")
  : false;

export const hasCustomerRight = rights => rights && Array.isArray(rights)
  ? rights.includes("COMPANYFILTER")
  : false;

export const sortAlphabeticalAsc = (a, b) => a.label.localeCompare(b.label);

export const isAppraisalCase = caseHeader => !!caseHeader.caseInfo.appraisalSurveyInfo;

export const getFRSortFunc = () => {
  let stripDesignations = s => s
    .replace(/\([^()]*\)/g, "")
    .replace(/\[[^()]*\]/g, "")
    .replace(/\-|\(|\)|\[|\]|\*/g, "")
    .trim();

  // Have to get the label, strip it, and then make a new object with a label because the sort expects that
  return (a, b) => sortAlphabeticalAsc({ label: stripDesignations(a.label) }, { label: stripDesignations(b.label) });
};

export const getCaseType = customerType => {
  if (customerType === NEW_LENDER || customerType === EXISTING_LENDER) return caseTypes.valuation;
  return caseTypes.insurance;
};

export const isNumeric = (val) => Number(parseFloat(val)) == val;

// used for the excel export to make columns have the number type
// mostly taken from https://stackoverflow.com/questions/48535736/sheetjs-how-to-format-column
export const formatNumberColumn = (worksheet, col, format) => {
  const range = xlsx.utils.decode_range(worksheet["!ref"]);
  // skip the header row
  for (let row = range.s.r + 1; row <= range.e.r; ++row) {
    const ref = xlsx.utils.encode_cell({ r: row, c: col });
    if (worksheet[ref]) {
      worksheet[ref].t == "n";
      if (format) worksheet[ref].z = format;
    }
  }
};

export const formatWorksheetColumns = (columnDefinitions, worksheet) =>
  columnDefinitions.forEach((c, i) => {
    switch (c.dataType) {
      case DATA_TYPE_NUMBER_RAW:
      case DATA_TYPE_NUMBER_UNFORMATTED:
      case DATA_TYPE_NUMBER:
        formatNumberColumn(worksheet, i);
        break;
      case DATA_TYPE_NUMBER_HUNDREDTHS:
        formatNumberColumn(worksheet, i, "#,##0.00");
        break;
      case DATA_TYPE_NUMBER_TENTHS:
        formatNumberColumn(worksheet, i, "#,##0.0");
        break;
      case DATA_TYPE_MONEY:
        formatNumberColumn(worksheet, i, "$#,##0.00");
        break;
    }
  });

export const isValOrCusRole = (roles) => roles && Array.isArray(roles) 
? roles.includes("Customer") || roles.includes("Customer Admin") || roles.includes("ValuationCustomer") || roles.includes("ValuationCustomerManager")
: false;

// address is expected to be the "StreetNumber StreetName StreetSuffix City, State Zip")
export const openGoogleMapsLink = address => {
  let addressForUrl = address.replace(/\s/g, "+");
  let url = `https://www.google.com/maps/place/${addressForUrl}`;
  window.open(url, "_blank");
};

export const setObjectValue = (obj, field, value) => {
  let arr = field.split(".");
  let lastField = arr.pop();

  obj = arr.reduce((acc, val) => acc[val] ??= {}, obj);
  obj[lastField] = value;
};

/**
 * Clamp a number between 'min' and 'max'.
 * @type {(value: number, min: number, max: number) => number}
 */
export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
